import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type { ListResponse, ProcurementProforma, ProcurementProformaItemLock } from '@/types/api'
import type { RequestOptions } from '../crud'

export type ProcurementProformaItemInput = {
  id?: string
  item_type: 'product' | 'product_group'
  product_id?: string
  product_group_id?: string
  container_type_id?: string
  weight?: string
  container_count?: string
  unit_price: string
  /** «Цена завода»: what the trader pays the factory (per kg on the server). */
  factory_price?: string
  note?: string
}

export type ProcurementProformaStageInput = {
  id?: string
  percentage: string
  sub_statuses: { id?: string; status_template_id: string; order_number: number }[]
  /** From the server only: this stage cannot change any more. */
  locked?: boolean
}

export type ProcurementProformaInput = {
  proforma_name: string
  seller_document_number: string
  proforma_date: string
  proforma_type: 'container' | 'truck'
  procurement_plan_id: string
  seller_id: string
  factory_id: string
  buyer_id: string
  declarant_id?: string
  payment_term_id: string
  currency_id?: string
  note?: string
  status: 'draft' | 'approved' | 'cancelled'
  items: ProcurementProformaItemInput[]
  statuses: ProcurementProformaStageInput[]
  /** «Дата оплаты» of the first status (YYYY-MM-DD, '' none): a calendar reminder only. */
  first_stage_payment_date?: string
}

export type ProcurementProformaUpdate = ProcurementProformaInput & { version: number }

const base = '/procurement/proformas'

export const procurementProformasService = {
  path: 'procurement/proformas',
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementProforma>>(base, { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementProforma>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async create(body: ProcurementProformaInput) {
    const { data } = await http.post<ProcurementProforma>(base, body)
    return data
  },
  async update(id: string, body: ProcurementProformaUpdate) {
    const { data } = await http.put<ProcurementProforma>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  /** Only the stage 1 «Дата оплаты»: saved without the full Proforma checks, so an older Proforma takes it too. */
  async setPaymentDate(id: string, body: { version: number; first_stage_payment_date: string }) {
    const { data } = await http.patch<ProcurementProforma>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async remove(id: string) {
    const current = await this.get(id)
    await http.delete(`${base}/${encodeURIComponent(id)}`, { params: { version: current.version } })
  },
  async itemLock(id: string) {
    const { data } = await http.get<ProcurementProformaItemLock>(`${base}/${encodeURIComponent(id)}/item-lock`)
    return data
  },
}
